/* =========================================================
   MEZUZAH THEME-WIDE JS
   Counterpart to mezuzah-global.css. Loaded from theme.liquid
   on every page, deferred. Keep this file small: anything that
   belongs to one template belongs in that template's own asset.
   ========================================================= */

/* ---------------------------------------------------------
   JUDGE.ME HEADING DEMOTION SHIM (2026-07-21)
   Judge.me renders its write-review modal with real h1/h2/h3
   tags. Those tags are app-owned markup we cannot edit at
   source, and they inject themselves into the page's heading
   outline, so the first heading a crawler or outline tool sees
   on a PDP can be the modal's, not the product H1.

   This rewrites those tags to <p> after the app paints,
   copying every attribute across so Judge.me's own classes,
   ids and aria wiring (including any aria-labelledby target)
   keep resolving.

   Exact script as CTO-tested on the preview. Three things a
   future reader should know before touching it:

   1. The observer is deliberately broad, documentElement with
      subtree, because the modal is injected late and can be
      re-rendered at any time. It converges rather than looping:
      the second pass finds no h1/h2/h3 and mutates nothing. The
      cost is a querySelectorAll on every DOM mutation site-wide.
   2. Demoting a dialog heading to <p> removes its heading role,
      which is a real screen-reader navigation loss. The
      attribute copy keeps labelling intact, but a screen reader
      user can no longer jump to it as a heading. Accepted here
      as an outline-correctness tradeoff, not a free win.
   3. If Judge.me styles those nodes with bare tag selectors
      rather than classes, demotion changes their appearance.
      Verified on the PDP at implementation time.
   --------------------------------------------------------- */
(function () {
  function demote() {
    document.querySelectorAll('.jdgm-write-review-modal h1, .jdgm-write-review-modal h2, .jdgm-write-review-modal h3').forEach(function (h) {
      var p = document.createElement('p');
      for (var i = 0; i < h.attributes.length; i++) { p.setAttribute(h.attributes[i].name, h.attributes[i].value); }
      p.innerHTML = h.innerHTML;
      h.replaceWith(p);
    });
  }
  new MutationObserver(demote).observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('DOMContentLoaded', demote);
})();

/* ---------------------------------------------------------
   CART QUANTITY RACE GUARD (ERRORS.md B10, 2026-07-21)
   Rapid clicks on the cart drawer quantity +/- buttons were
   being silently dropped: 3 fast clicks netted +1.

   Root cause, per the B10 investigation: each click calls
   input.stepUp() (correct, the DOM value moves immediately)
   and then dispatches a `change` with no debounce and no
   in-flight guard. Every change independently POSTs the input's
   CURRENT value to /cart/change as an ABSOLUTE quantity. Under
   rapid clicking several requests are in flight at once, each
   carrying a different snapshot, and response order is not
   guaranteed to match click order, so an earlier, lower
   snapshot can resolve last and win.

   The fix is a debounce, not a block. Blocking clicks while a
   request is in flight would also lose clicks, which is the
   very symptom. Here every click still increments the input,
   so the number stays responsive; only the network write is
   coalesced, and it carries the final value.

   Implemented as a capture-phase interceptor because the
   offending listener lives in bundled theme.js, which we do not
   edit. We swallow the app's change, then re-dispatch one after
   the clicks stop, flagged so we let our own through.
   --------------------------------------------------------- */
(function () {
  var DELAY = 350;
  var timers = new WeakMap();
  var reentry = new WeakSet();

  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || input.tagName !== 'INPUT' || typeof input.closest !== 'function') return;
    if (!input.closest('quantity-input, .quantity')) return;
    // cart surfaces only: the PDP quantity picker writes nothing until add-to-cart
    if (!input.closest('.drawer, .cart, cart-drawer, form[action*="/cart"]')) return;

    if (reentry.has(input)) { reentry.delete(input); return; }

    e.stopImmediatePropagation();
    clearTimeout(timers.get(input));
    timers.set(input, setTimeout(function () {
      reentry.add(input);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }, DELAY));
  }, true);
})();

/* ---------------------------------------------------------
   CART FETCH RESILIENCE (WO-201, 2026-08-10; rewritten WO-342, 2026-09-07)
   Original bug (WO-201): when Cloudflare rate-limits a cart request, it
   answers with an HTML challenge page instead of JSON. theme.js's own
   cart code (bundled, not ours to edit) always does
   `.then(response => response.json())` with no content-type check,
   so JSON.parse throws on the HTML body and the customer sees a raw
   "Unexpected token '<'" in the buy column instead of any message.

   WO-342 rule: this wrapper's job is now strictly limited to two
   things, and nothing else --
   (1) if the fetch itself never comes back with a response at all
       (a real network failure -- TypeError, offline, DNS, etc.), retry
       ONCE, and only for /cart/add. Never for /cart/change or
       /cart/update, since both of those write an ABSOLUTE line
       quantity, not a delta -- replaying one after the customer has
       since changed the quantity again silently overwrites their
       later input with a stale one.
   (2) if a response IS received but its body isn't valid JSON (the
       original Cloudflare-challenge case above), show the friendly
       banner once, with NO retry.

   Every other received response -- 2XX, or any 4XX/5XX with a valid
   JSON body, which includes Shopify's own real business-rule errors
   (sold out, over the available quantity, quantity rule violations)
   -- is handed straight back to the caller untouched. theme.js's own
   per-endpoint handling already reads that body itself (addToCartError
   for /cart/add, the parsedState.errors check in updateCart for
   /cart/change) and shows Shopify's real message through the same
   `[data-cart-errors-container]` slot this wrapper's own friendly
   banner uses -- this file no longer intercepts that path or shows
   its own message for it.

   WO-342's own finding, not previously reported: the OLD version of
   this wrapper had no `.catch` at all, so a genuine network failure
   (fetch() itself rejecting) was never retried, while every RECEIVED
   response that wasn't `ok`, including a real, valid Shopify 422
   business error, WAS unconditionally replayed 3 seconds later with
   the exact same body. On /cart/add that replay could add the same
   line a second time (Shopify's own documented behavior: an overstock
   add adds up to the available maximum then returns 422, so the
   retry's second POST adds again); on /cart/change or /cart/update,
   replaying a stale absolute quantity 3 seconds later could silently
   overwrite whatever the customer had changed it to in the meantime.
   That is the mechanism behind the reported vanishing cart lines. This
   rewrite inverts both halves: never replay a response that arrived,
   retry (once, /cart/add only) only when no response arrived at all.

   Scope: matches the AJAX Cart API's three JSON write endpoints only.
   Checked live (window.theme.routes on the pushed preview) rather than
   assuming the commonly-documented Shopify ".js" suffix convention:
   this theme's cart_add_url/cart_change_url/cart_update_url are
   suffix-less (/cart/add, /cart/change, /cart/update), content-
   negotiated via an Accept: application/json header instead of the
   URL. Matches those three paths only -- NOT bare /cart (cart_url),
   used for real page navigation and for theme.js's own getCart() fetch
   (.../cart?section_id=..., consumed as text, not JSON, per
   theme.dev.js) -- and not /cart/clear, which no code in this theme
   calls (grepped assets/ and every .liquid file, zero matches; the
   original CART_JS_RE never matched it either).

   Message target unchanged from WO-201: theme.js's own
   `[data-cart-errors-container]` / `.errors` markup
   (snippets/product-buttons.liquid, upsell-product.liquid -- the buy
   column's native error slot, toggled via classList.add/remove
   ('is-visible') exactly as theme.js's own addToCartError does it,
   verified in theme.dev.js). Reusing it means no new CSS and no visual
   drift from the theme's existing error banner.
   --------------------------------------------------------- */
(function () {
  var CART_JS_RE = /\/cart\/(add|change|update)(\.js)?(\?|$)/;
  var CART_ADD_RE = /\/cart\/add(\.js)?(\?|$)/;
  var FRIENDLY_MESSAGE = 'One moment, the cart is busy. Please try again.';
  var nativeFetch = window.fetch.bind(window);

  function requestUrl(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    return '';
  }

  function matchesCartPath(input, re) {
    try {
      return re.test(new URL(requestUrl(input), window.location.origin).pathname);
    } catch (e) {
      return false;
    }
  }

  function isCartJsonEndpoint(input) {
    return matchesCartPath(input, CART_JS_RE);
  }

  function isCartAddEndpoint(input) {
    return matchesCartPath(input, CART_ADD_RE);
  }

  function hasValidJsonBody(response) {
    return response
      .clone()
      .text()
      .then(
        function (text) {
          try {
            JSON.parse(text);
            return true;
          } catch (e) {
            return false;
          }
        },
        function () {
          return false;
        }
      );
  }

  function toggleFriendlyMessage(show) {
    document.querySelectorAll('[data-cart-errors-container]').forEach(function (el) {
      if (show) el.innerHTML = '<div class="errors">' + FRIENDLY_MESSAGE + '</div>';
      el.classList.toggle('is-visible', show);
    });
  }

  window.fetch = function (input, init) {
    if (!isCartJsonEndpoint(input)) return nativeFetch(input, init);

    toggleFriendlyMessage(false);

    var canRetryOnNetworkFailure = isCartAddEndpoint(input);

    return nativeFetch(input, init)
      .catch(function (networkError) {
        if (!canRetryOnNetworkFailure) throw networkError;
        return nativeFetch(input, init);
      })
      .then(function (response) {
        // A response was received (first try, or the one network-failure
        // retry above) -- never replay again from here, whatever its
        // status. Only surface our own message when the body isn't valid
        // JSON at all; anything else goes straight back to the caller so
        // the caller's own error handling shows Shopify's real message.
        return hasValidJsonBody(response).then(function (valid) {
          if (!valid) toggleFriendlyMessage(true);
          return response;
        });
      });
  };
})();

/* Mobile filter groups: native default_open is global (no breakpoint
   split) so the mobile drawer opens every group. Collapse them <=749px
   while desktop stays open. Groups remain tappable via data-collapsible.
   ISS pre-flip sweep 2026-07-25. */
(function () {
  var mq = window.matchMedia('(max-width: 749px)');
  function collapse() {
    if (!mq.matches) return;
    var groups = document.querySelectorAll('.collection__sidebar__slider details[data-collapsible][open]');
    groups.forEach(function (d) {
      d.removeAttribute('open');
      var body = d.querySelector('[data-collapsible-body]');
      if (body) { body.style.height = '0px'; }
    });
  }
  if (document.readyState !== 'loading') collapse();
  else document.addEventListener('DOMContentLoaded', collapse);
  window.addEventListener('load', collapse);
})();

/* ---------------------------------------------------------
   SCROLL-LOCK WATCHDOG (WO-213 P1, 2026-08-12)
   Owner-reported: landing on a PDP at 375px can leave the viewport
   stuck on the photo area -- vertical swipes do nothing. Reproduced
   live on the pushed V6 preview by racing the drawer's own open/close
   transition, not by anything gallery-specific:

   1. Open the mobile drawer (tap the hamburger).
   2. Tap .drawer__close before the open transition settles (<~150ms
      after open). The close click is swallowed -- the drawer keeps
      its is-open class, never actually closes -- AND
      document.documentElement keeps data-scroll-locked forever.
      Confirmed via direct event instrumentation: theme:scroll:lock
      fires, theme:scroll:unlock never does. Waiting ~600ms before
      closing (letting the transition finish) closes correctly and
      fires the matching unlock every time.
   3. Ruled out first, so this isn't guesswork: touch-action and
      overscroll-behavior stay "auto" throughout (never change), and
      Event.prototype.preventDefault was patched globally during
      testing and never fired for any touchmove -- the failure is
      entirely the stuck data-scroll-locked attribute, not a CSS or
      JS touch-blocking mechanism.

   theme.js (bundled Broadcast core, not ours to edit) owns the
   drawer/popup/modal open+close wiring and the scroll-lock library
   itself, so the actual race can't be patched at its source from
   here. Two things worth recording about the fix that DIDN'T work,
   since the wrong one is tempting: dispatching 'theme:scroll:unlock'
   directly (the theme's own public event, matches what a correct
   close does) DOES clear data-scroll-locked and the underlying
   library's own state -- but the swallowed close click also never
   removed the drawer's is-open class or its overlay, and that overlay
   (position:fixed, full-viewport, still layered above the page) goes
   on physically intercepting touch input regardless of whether
   scroll is "unlocked" underneath it. Unlocking the flag without
   also closing the drawer fixes nothing a user can feel.

   What actually works, verified live: after the delay, if the page
   is still marked locked, RE-DISPATCH A CLICK on the SAME close
   button the user already tapped. By then the transition window that
   swallowed the first click has passed, so the retry goes through
   theme.js's own normal close handling and everything -- the is-open
   class, the overlay, the scroll lock -- clears together, correctly,
   through the real code path instead of a hand-patched approximation
   of it.
   --------------------------------------------------------- */
(function () {
  var CHECK_DELAY = 500; // longer than the ~150-600ms window the race lives in
  var POLL_INTERVAL = 1000;

  // WO-654 F19: the desktop header search overlay also holds the scroll lock (SearchPopdown.open() locks, and marks
  // header-search-popdown.is-open), so it counts as open here. The drawer's own search popdown inside mobile-menu is not
  // an overlay and does not count (the mobile menu search guard at the end of this file clears it).
  function searchOverlayOpen() {
    var pops = document.querySelectorAll('header-search-popdown.is-open');
    for (var i = 0; i < pops.length; i++) {
      if (!pops[i].closest('mobile-menu')) return true;
    }
    return false;
  }

  function anythingActuallyOpen() {
    return !!(
      document.querySelector('.drawer.is-open, header-drawer.is-open') ||
      document.querySelector('dialog[open]') ||
      document.querySelector('popup-component .is-open, .popup.is-open') ||
      searchOverlayOpen()
    );
  }

  // Retries the user's own close click through the theme's real close
  // handler once the swallow window has passed, rather than
  // hand-patching the lock flag (see comment above -- that clears the
  // flag but leaves the overlay physically blocking touch).
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var closer = e.target.closest('.drawer__close, [data-close-drawer], [data-popup-close], .product-modal__close');
    if (!closer) return;
    // WO-654 F19: re-click only when the close was really swallowed, i.e. the open drawer or popup holding this button
    // never lost is-open during the wait. If it closed and was opened again inside the 500 ms (tap X, tap the menu
    // straight away), the retry used to close the new menu by itself.
    var holder = closer.closest('.is-open');
    var closedInWait = false;
    var watcher = null;
    if (holder && window.MutationObserver) {
      watcher = new MutationObserver(function (records) {
        records.forEach(function (r) {
          if (r.oldValue && /(^|s)is-open(s|$)/.test(r.oldValue) && !r.target.classList.contains('is-open')) closedInWait = true;
        });
      });
      watcher.observe(holder, { attributes: true, attributeFilter: ['class'], attributeOldValue: true });
    }
    setTimeout(function () {
      if (watcher) watcher.disconnect();
      if (closedInWait) return;
      if (document.documentElement.hasAttribute('data-scroll-locked')) closer.click();
    }, CHECK_DELAY);
  }, true);

  // Backstop for paths with no close-button click to hang a retry off
  // (Escape key, overlay tap, programmatic close): no specific button
  // to re-click here, so fall back to the direct unlock event. Gated
  // on nothing being genuinely open, so it only fires for the "flag
  // stuck but the close otherwise worked" shape of this bug, not the
  // "close was swallowed entirely" shape the retry above handles.
  setInterval(function () {
    if (document.documentElement.hasAttribute('data-scroll-locked') && !anythingActuallyOpen()) {
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
    }
  }, POLL_INTERVAL);

  // bfcache restore (back/forward nav) can resurrect a stale locked
  // attribute from before the page was cached.
  window.addEventListener('pageshow', function () {
    if (document.documentElement.hasAttribute('data-scroll-locked') && !anythingActuallyOpen()) {
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
    }
  });
})();

/* ---------------------------------------------------------
   THIRD-PARTY SCROLL-LOCK CLEANUP (WO-232, 2026-08-12)
   klaviyo-prevent-body-scrolling is a class Klaviyo's own onsite
   popup script adds to <body> when it shows a popup, backed by a
   real overflow:hidden rule from Klaviyo's own injected
   stylesheet. It is not ours: confirmed by exhaustive grep, the
   class does not appear anywhere in this repo, in theme.js, or in
   vendor.js. Klaviyo's own close button does not reliably remove
   it -- reproduced live, closing a real popup left the class in
   place, and Klaviyo's own localStorage.klaviyoOnsite bookkeeping
   (disabledForms vs viewedForms) showed the form was never marked
   dismissed on their side either. That is a defect in their
   script, not ours, and it is not our place to patch a vendor's
   script. Full evidence: CTO-INBOX.md, WO-232.

   Our own drawer/cart/search scroll-lock (theme.js + vendor.js's
   bundled scroll-lock library) was independently verified correct
   in isolation: it locks and restores body.style.overflow cleanly
   every time, with no Klaviyo popup involved. The bug a shopper
   actually hits is that OUR drawer's unlock event fires and clears
   OUR lock, while KLAVIYO's separate, independent lock silently
   outlives it, and the page reads as permanently frozen even
   though our own code did everything right.

   Fix is defensive hardening of our own unlock path only, nothing
   Klaviyo-specific patched or touched: every time our theme
   unlocks scroll, force body.style.overflow back to '' (harmless
   if nothing else was holding it), then strip Klaviyo's class --
   but ONLY when no Klaviyo modal is actually visible right now.
   Klaviyo marks its own popup wrapper with
   data-kl-scroll-locking-modal="true" (confirmed live: the actual
   dialog element carries role="dialog" aria-modal="true" plus that
   attribute). If that's present with real dimensions, a Klaviyo
   popup is genuinely showing, and stripping the class would let
   the page scroll behind a live modal -- worse than the frozen
   page it replaces. Do nothing in that case and leave the class
   alone; Klaviyo's own close (or the watchdog above, if the whole
   drawer is stuck) can still clear things afterward.

   The setTimeout(...,0) below is deliberate, not decoration: it
   runs our cleanup after theme.js's own same-tick unlock handler
   (the vendor.js scroll-lock library's own restore-from-saved-
   value logic) has already finished, so our forced '' is always
   the last word regardless of listener registration order.
   --------------------------------------------------------- */
(function () {
  var KLAVIYO_LOCK_CLASS = 'klaviyo-prevent-body-scrolling';

  function klaviyoModalVisible() {
    var modal = document.querySelector('[data-kl-scroll-locking-modal="true"]');
    if (!modal) return false;
    var style = getComputedStyle(modal);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    var rect = modal.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  document.addEventListener('theme:scroll:unlock', function () {
    setTimeout(function () {
      document.body.style.overflow = '';
      if (!klaviyoModalVisible()) {
        document.body.classList.remove(KLAVIYO_LOCK_CLASS);
      }
    }, 0);
  });
})();

/* ---------------------------------------------------------
   MOBILE MENU SEARCH GUARD (WO-654 F01, 2026-09-30)
   Shay's bug (shay-22 to shay-24): open the mobile menu after a second page view and every row is blank.
   Cause: the drawer search sits in <details open> (sections/mobile-menu.liquid), which fires a toggle event
   once when the page parses. theme.js's header-search-popdown answers that event with open(): it adds is-open
   and dispatches theme:scroll:lock. When theme.js is already cached and running by then (the second page view)
   the popdown is left "open" behind the drawer: header-dropdown.css hides the rows and the page scroll stays
   locked. The drawer search is never an overlay, so this removes is-open from that one popdown whenever it
   appears and, if nothing else is open, releases the lock. The class removal is safe: .mobile-menu
   .search-popdown is forced to opacity 1 (mezuzah-global.css), so no opacity transition runs and theme.js's
   transitionend handler never closes the <details>. The desktop header search is a different popdown (not
   inside mobile-menu) and is untouched.
   --------------------------------------------------------- */
(function () {
  var watched = null;

  // any drawer, dialog or popup that is genuinely open owns the scroll lock (same list the watchdog above uses)
  function drawerOpen() {
    return !!document.querySelector('.drawer.is-open, header-drawer.is-open, dialog[open], popup-component .is-open, .popup.is-open');
  }

  function release() {
    var pop = document.querySelector('mobile-menu header-search-popdown');
    if (!pop || !pop.classList.contains('is-open')) return;
    pop.classList.remove('is-open');
    unlockIfIdle();
    // the lock event that came with the class can land a moment later than the class itself; look once more
    setTimeout(unlockIfIdle, 400);
  }

  function unlockIfIdle() {
    var pop = document.querySelector('mobile-menu header-search-popdown');
    if (drawerOpen() || (pop && pop.classList.contains('is-open'))) return;
    if (document.documentElement.hasAttribute('data-scroll-locked')) {
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
    }
  }

  function watch() {
    var pop = document.querySelector('mobile-menu header-search-popdown');
    if (!pop || pop === watched) return;
    watched = pop;
    new MutationObserver(release).observe(pop, { attributes: true, attributeFilter: ['class'] });
    release();
  }

  if (document.readyState !== 'loading') watch();
  else document.addEventListener('DOMContentLoaded', watch);
  window.addEventListener('pageshow', watch);
})();

/* ---------------------------------------------------------
   CART DRAWER GIFT BOX IN VIEW (WO-660 item 4, 2026-09-30)
   Ticking "This order is a gift" in the cart drawer opens the message box (mezuzah-global.css .mez-gift-widget.is-open). When the opened box sits
   below the visible part of the drawer body, scroll the body (smoothly) just far enough to show all of it; nothing happens if it is already in view.
   Delegated on the document so it survives the drawer being re-rendered after a cart change. Drawer only.
   --------------------------------------------------------- */
(function () {
  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || input.type !== 'checkbox' || !input.closest || !input.closest('.drawer--cart .cart__gift-check')) return;
    if (!input.checked) return;
    setTimeout(function () {
      var body = input.closest('.drawer__body');
      var widget = input.closest('.mez-gift-widget') || input.closest('.cart__gift-card');
      var box = widget && (widget.querySelector('.cart__field--textarea') || widget);
      if (!body || !box || !box.offsetHeight) return;
      var b = body.getBoundingClientRect();
      var r = box.getBoundingClientRect();
      var over = r.bottom - b.bottom + 16;
      if (over > 0) body.scrollBy({ top: over, behavior: 'smooth' });
    }, 350);
  });
})();
